import React from "react";
import { Button } from "@/components/ui/button";
import { formatIdDateTime } from "@/lib/format";
import { CheckCircle, XCircle, ClockClockwise } from "@phosphor-icons/react";
import { FIELD_LABEL } from "./constants";

/**
 * CorrectionRow — one entry in the mismatch corrections list. Shows status
 * badge, submitter, proposed diffs, decision context, and action buttons
 * (approve/reject for super admin, cancel-draft for the original submitter).
 */
export default function CorrectionRow({
  c,
  isSuperAdmin,
  currentEmail,
  onDecide,
  onCancelDraft,
}) {
  const badge = c.status === "pending"
    ? { cls: "bg-amber-200 text-amber-900", label: "PENDING" }
    : c.status === "approved"
      ? { cls: "bg-emerald-200 text-emerald-900", label: "APPROVED" }
      : { cls: "bg-rose-200 text-rose-900", label: "REJECTED" };
  const isOwnDraft = c.status === "pending" && c.submitted_by?.email === currentEmail;

  return (
    <div className="p-4 text-sm space-y-2" data-testid={`correction-row-${c.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`px-2 py-0.5 text-xs font-mono rounded-full ${badge.cls}`}>{badge.label}</span>
        <span className="text-muted-foreground text-xs">
          Diajukan oleh <span className="font-semibold text-foreground">{c.submitted_by?.name || c.submitted_by?.email}</span>
          {" · "}{formatIdDateTime(c.submitted_at)}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
        {Object.entries(c.proposed_changes || {}).map(([k, v]) => (
          <div key={k} className="text-xs font-mono">
            <span className="text-muted-foreground">{FIELD_LABEL[k] || k}:</span>{" "}
            <span className="line-through opacity-60">{String(c.original_values?.[k] ?? "-")}</span>
            <span className="mx-1">→</span>
            <span className="font-bold">{String(v)}</span>
          </div>
        ))}
      </div>
      {c.reason && <div className="text-xs italic text-muted-foreground">"{c.reason}"</div>}
      {c.decided_by && (
        <div className="text-xs text-muted-foreground border-t border-border pt-2">
          {c.status === "approved"
            ? <CheckCircle size={12} className="inline text-emerald-700 mr-1" />
            : <XCircle size={12} className="inline text-rose-700 mr-1" />}
          {c.status === "approved" ? "Disetujui" : "Ditolak"} oleh <span className="font-semibold">{c.decided_by?.name || c.decided_by?.email}</span>
          {" · "}{formatIdDateTime(c.decided_at)}
          {c.decision_note && <span> — "{c.decision_note}"</span>}
        </div>
      )}
      {c.status === "pending" && (
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          {isSuperAdmin && (
            <>
              <Button size="sm" onClick={() => onDecide("approve")} data-testid={`approve-${c.id}`}>
                <CheckCircle size={14} className="mr-1" /> Setujui
              </Button>
              <Button size="sm" variant="destructive" onClick={() => onDecide("reject")} data-testid={`reject-${c.id}`}>
                <XCircle size={14} className="mr-1" /> Tolak
              </Button>
            </>
          )}
          {isOwnDraft && (
            <Button size="sm" variant="ghost" onClick={onCancelDraft} data-testid={`cancel-${c.id}`}>
              <ClockClockwise size={14} className="mr-1" /> Batalkan draft
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
